import { z } from "zod";

import {
  AddressSchema,
  IsoTimestampSchema,
  LocaleSchema,
  LocalizedTextSchema,
  MoneyHalalasSchema,
  PhoneSchema,
  ReturnReferenceSchema,
} from "./common";
import { SourceChannelSchema } from "./order";
import {
  CollectionMethodSchema,
  LineReasonCodeSchema,
  PolicySnapshotSchema,
  ResolutionTypeSchema,
} from "./policy";

/** docs/04 — state lives on the LINE, never the header (ADR-005). */
export const LineStateSchema = z.enum([
  "requested",
  "approved",
  "awaiting_pickup",
  "in_transit",
  "received",
  "inspected",
  /**
   * The carrier refused or failed the booking. Still in flight, NOT settled: it exists so
   * that "not booked yet" (`approved`) and "booking failed, twice" are distinguishable, and
   * so the failure is recorded rather than inferred from an absent AWB. Retry leads onward
   * to `awaiting_pickup`. TODO(decision):Q7 — no carrier-specific mechanism is implied.
   */
  "booking_failed",
  // terminal
  "restocked",
  "quarantined",
  "disposed",
  // exit
  "rejected",
  /** The merchant closed the return after review: no goods outcome, no refund. */
  "completed",
  /**
   * The **shopper** withdrew their own return (or the merchant did so on their behalf).
   * Deliberately distinct from `rejected`: different actor, different meaning, and no
   * blame-assigning reason. Conflating the two puts a merchant refusal on the tracking page
   * of a shopper who cancelled, and miscounts the merchant's rejection rate.
   */
  "cancelled",
  "lost_in_transit",
  "expired",
]);
export type LineState = z.infer<typeof LineStateSchema>;

/** docs/04 — the three legal entry points. Never synthesise a fake `requested` step for sweeps. */
export const EntryStateSchema = z.enum([
  "requested",
  "approved",
  "in_transit",
  "received",
]);
export type EntryState = z.infer<typeof EntryStateSchema>;

/**
 * docs/03 — set by the warehouse. Enum, not a boolean and not free text.
 * Matches the condition vocabulary already live on the platform; anything
 * finer the warehouse wants to say goes in the free-text warehouse note.
 */
export const GradeSchema = z.enum(["sellable", "damaged", "missing"]);
export type Grade = z.infer<typeof GradeSchema>;

export const DispositionSchema = z.enum(["restock", "quarantine", "dispose"]);
export type Disposition = z.infer<typeof DispositionSchema>;

/** docs/03 — `return_evidence.uploaded_by`. */
export const EvidenceUploaderSchema = z.enum([
  "customer",
  "warehouse",
  "merchant",
]);
export type EvidenceUploader = z.infer<typeof EvidenceUploaderSchema>;

/** docs/03 — `return_event.actor_type`. */
export const ActorTypeSchema = z.enum([
  "customer",
  "merchant",
  "warehouse",
  "system",
  "carrier",
]);
export type ActorType = z.infer<typeof ActorTypeSchema>;

export const ActorSchema = z.object({
  type: ActorTypeSchema,
  id: z.string().nullable().optional(),
});
export type Actor = z.infer<typeof ActorSchema>;

/** docs/03 — `return_line`. */
export const ReturnLineSchema = z
  .object({
    id: z.string().min(1),
    returnOrderId: z.string().min(1),
    /** Resolved via the adapter's mapping. Null until matched. */
    sirdabSkuId: z.string().nullable(),
    externalProductRef: z.string().min(1),
    variant: z.string().nullable(),
    /** Snapshot for display — the product may be renamed later. */
    productName: LocalizedTextSchema,
    /** Snapshot, integer halalas. Needed for credit-note data. */
    unitPrice: MoneyHalalasSchema,
    /** What the shopper declared. NEVER reconciled against qtyReceived. */
    qtyExpected: z.number().int().positive(),
    /** What actually arrived. Null until receipt. NEVER overwrites qtyExpected — the gap is the data. */
    qtyReceived: z.number().int().nonnegative().nullable(),
    /**
     * A shopper's choice OR a system-originated cause (`cod_refused`, `delivery_failed`).
     * The two vocabularies stay separate so a sweep is never displayed as a shopper's
     * reason. See `LineReasonCodeSchema`.
     */
    reasonCode: LineReasonCodeSchema,
    subReason: z.string().nullable(),
    /**
     * The shopper's own words for this line, verbatim. Optional everywhere except under
     * the `other` reason, where submission refuses without it. Rendered to the merchant in
     * the queue; never translated.
     */
    note: z.string().nullable(),
    state: LineStateSchema,
    /** Set by the warehouse. Null until inspected. */
    grade: GradeSchema.nullable(),
    disposition: DispositionSchema.nullable(),
    /** Renders to the shopper. Mandatory when state is `rejected` (docs/04). */
    rejectionReason: LocalizedTextSchema.nullable(),
    createdAt: IsoTimestampSchema,
    updatedAt: IsoTimestampSchema,
  })
  .refine(
    (line) => line.state !== "rejected" || line.rejectionReason !== null,
    {
      message: "rejectionReason is required when a line is rejected",
      path: ["rejectionReason"],
    },
  )
  // A cancellation is the shopper's own decision. Attaching a rejection reason to it would
  // put words in the merchant's mouth on the shopper's own tracking page.
  .refine(
    (line) => line.state !== "cancelled" || line.rejectionReason === null,
    {
      message:
        "a cancelled line carries no rejectionReason — nobody refused it",
      path: ["rejectionReason"],
    },
  );
export type ReturnLine = z.infer<typeof ReturnLineSchema>;

/** docs/03 — `return_evidence`. */
export const ReturnEvidenceSchema = z.object({
  id: z.string().min(1),
  returnLineId: z.string().min(1),
  uploadedBy: EvidenceUploaderSchema,
  photoUrl: z.string().min(1),
  note: z.string().nullable(),
  createdAt: IsoTimestampSchema,
});
export type ReturnEvidence = z.infer<typeof ReturnEvidenceSchema>;

/** docs/03 — `return_event`. Append-only: no update or delete path may exist in code. */
export const ReturnEventSchema = z.object({
  id: z.string().min(1),
  returnOrderId: z.string().min(1),
  /** Null for header-level events. */
  returnLineId: z.string().nullable(),
  actorType: ActorTypeSchema,
  actorId: z.string().nullable(),
  /** Null on non-transition events. */
  fromState: LineStateSchema.nullable(),
  toState: LineStateSchema.nullable(),
  /** `created`, `approved`, `photo_added`, ... — deliberately an open vocabulary (docs/03). */
  eventType: z.string().min(1),
  payload: z.record(z.string(), z.unknown()),
  createdAt: IsoTimestampSchema,
});
export type ReturnEvent = z.infer<typeof ReturnEventSchema>;

/** docs/03 — `return_order`. Header carries request-level facts, never the state. */
export const ReturnOrderSchema = z.object({
  id: z.string().min(1),
  reference: ReturnReferenceSchema,
  merchantId: z.string().min(1),
  sourceChannel: SourceChannelSchema,
  /** Soft link. Not a foreign key — we do not own the orders table. */
  externalOrderRef: z.string().min(1),
  customerName: z.string().min(1),
  customerPhone: PhoneSchema,
  customerAddress: AddressSchema,
  /** Frozen at creation. Never re-read live policy (ADR-006). */
  policySnapshot: PolicySnapshotSchema,
  resolutionType: ResolutionTypeSchema,
  /** Resolved from the snapshot at creation, integer halalas. Do not recompute later. */
  feeApplied: MoneyHalalasSchema,
  collectionMethod: CollectionMethodSchema,
  /** Decided at label time, not on arrival. */
  destinationFacilityId: z.string().nullable(),
  reverseAwb: z.string().nullable(),
  carrier: z.string().nullable(),
  locale: LocaleSchema,
  createdAt: IsoTimestampSchema,
  resolvedAt: IsoTimestampSchema.nullable(),
});
export type ReturnOrder = z.infer<typeof ReturnOrderSchema>;

/** A return with its lines. Header state is NEVER stored — derive it from the lines. */
export const ReturnOrderWithLinesSchema = z.object({
  order: ReturnOrderSchema,
  lines: z.array(ReturnLineSchema).min(1),
});
export type ReturnOrderWithLines = z.infer<typeof ReturnOrderWithLinesSchema>;

/**
 * The event types this codebase writes today, by name. `return_event.event_type` stays an
 * open `text` column (docs/03) and `ReturnEventSchema.eventType` stays an open string, so
 * this list is documentation for renderers, not a validator: a type absent from it still
 * stores and still comes back, and a client renders it by its raw type rather than dropping
 * it.
 *
 * Two namespaces are reserved for events nothing writes yet, so the carrier and warehouse
 * integrations have a place to land without a schema change:
 *
 *  - `carrier.*` (`carrier.tracking`, `carrier.exception`, ...) for what the courier said.
 *  - `inspection.*` (`inspection.result`, `inspection.photo`, ...) for what the dock found.
 *
 * Their payloads are deliberately NOT modelled here. Shape them when the integration
 * exists (TODO(decision):Q7 for carriers), then add a projection in `toShopperEvent`.
 */
export const KNOWN_RETURN_EVENT_TYPES = [
  "created",
  "requested",
  "approved",
  "pickup_booked",
  "collected",
  "received",
  "graded",
  "booking_failed",
  "cancelled",
  "restocked",
  "quarantined",
  "disposed",
  "rejected",
  "lost_in_transit",
  "expired",
  "completed",
] as const;
export type KnownReturnEventType = (typeof KNOWN_RETURN_EVENT_TYPES)[number];

export const CARRIER_EVENT_PREFIX = "carrier.";
export const INSPECTION_EVENT_PREFIX = "inspection.";

/**
 * What a shopper is told about one event. Narrower than `ReturnEventSchema` on purpose:
 * no ids of any kind (`actorId` names a merchant user or a service), no raw payload
 * (webhook bodies, consent records, carrier failure detail), and the line is named by the
 * `lineRef` the shopper saw at lookup rather than our row id.
 *
 * `detail` is an allowlist, field by field, of the facts the shopper may read. Adding a
 * field here is a product decision, not a convenience.
 */
export const ShopperEventDetailSchema = z.object({
  /** The shopper's own words on the line, recorded at creation. Verbatim, never translated. */
  note: z.string().optional(),
  /** How many photos the shopper attached at creation. */
  photoCount: z.number().int().nonnegative().optional(),
  /** Renders in full on a `rejected` step (docs/04). */
  rejectionReason: LocalizedTextSchema.optional(),
  /** What the dock counted. Shown next to what was declared; never reconciled (docs/03). */
  qtyReceived: z.number().int().nonnegative().optional(),
  /** Only once the line has actually been inspected (docs/06). */
  grade: GradeSchema.optional(),
});
export type ShopperEventDetail = z.infer<typeof ShopperEventDetailSchema>;

export const ShopperEventSchema = z.object({
  /** `return_event.event_type`, verbatim. Open vocabulary: render unknown types by name. */
  type: z.string().min(1),
  at: IsoTimestampSchema,
  actorType: ActorTypeSchema,
  /** The store order's line reference. Null for a header-level event. */
  lineRef: z.string().nullable(),
  fromState: LineStateSchema.nullable(),
  toState: LineStateSchema.nullable(),
  /** Server-authored, bilingual. Rendered, never translated by the client. */
  label: LocalizedTextSchema,
  detail: ShopperEventDetailSchema,
});
export type ShopperEvent = z.infer<typeof ShopperEventSchema>;

/** docs/06 — one step of the shopper-facing tracking timeline. */
export const TimelineStepSchema = z.object({
  state: LineStateSchema,
  at: IsoTimestampSchema,
  label: LocalizedTextSchema,
  lineRef: z.string().nullable().optional(),
});
export type TimelineStep = z.infer<typeof TimelineStepSchema>;
